import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Car, Shield, Clock, Star, MapPin, Users } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const features = [
    {
      icon: <Car className="h-8 w-8" />,
      title: { en: 'Multiple Car Types', ar: 'أنواع متعددة من السيارات' },
      description: { en: 'Choose from economy, comfort, or premium rides', ar: 'اختر من السيارات الاقتصادية أو المريحة أو الفاخرة' }
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: { en: 'Safe & Secure', ar: 'آمن ومأمون' },
      description: { en: 'Verified drivers and secure payment options', ar: 'سائقون موثوقون وخيارات دفع آمنة' }
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: { en: '24/7 Available', ar: 'متاح 24/7' },
      description: { en: 'Book rides anytime, anywhere in Syria', ar: 'احجز رحلات في أي وقت وفي أي مكان في سوريا' }
    },
    {
      icon: <Star className="h-8 w-8" />,
      title: { en: 'Top Rated Drivers', ar: 'سائقون مميزون' },
      description: { en: 'Highly rated and professional drivers', ar: 'سائقون محترفون ذوو تقييمات عالية' }
    }
  ];
  
  const stats = [
    { value: '15K+', label: { en: 'Active Users', ar: 'مستخدم نشط' }, icon: <Users className="h-5 w-5" /> },
    { value: '850+', label: { en: 'Drivers', ar: 'سائق' }, icon: <Car className="h-5 w-5" /> },
    { value: '45K+', label: { en: 'Rides', ar: 'رحلة' }, icon: <MapPin className="h-5 w-5" /> },
    { value: '4.8', label: { en: 'Rating', ar: 'تقييم' }, icon: <Star className="h-5 w-5" /> }
  ];
  
  const currentLang = t('language');
  const isArabic = currentLang === 'اللغة';
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-white to-black">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-gray-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/saeed-logo.png" alt="Saeed" className="h-11 w-11 object-contain bg-black rounded-lg p-1" />
            <h1 className="text-2xl font-bold tracking-wide">
              SAEED
            </h1>
          </div>
          <LanguageSwitcher />
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t('tagline')}
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            {t('description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-black to-black hover:from-black hover:to-black text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => navigate('/login')}
            >
              <Car className="mr-2 h-5 w-5" />
              {t('bookRide')}
            </Button>
            
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-green-500 text-black hover:bg-gray-50 px-8 py-6 text-lg rounded-xl transition-all duration-300"
              onClick={() => navigate('/login')}
            >
              <Users className="mr-2 h-5 w-5" />
              {t('becomeDriver')}
            </Button>
          </div>
          
          <div className="mt-8">
            <Button 
              variant="ghost"
              className="text-gray-600 hover:text-gray-900"
              onClick={() => navigate('/login')}
            >
              {t('adminPanel')}
            </Button>
          </div>
        </div>
      </section>
      
      {/* Stats Section */}
      <section className="py-12 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label.en} className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <div className="text-black">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{isArabic ? stat.label.ar : stat.label.en}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl font-bold text-center mb-16 text-gray-900">
            {isArabic ? 'لماذا تختار سعيد تاكسي؟' : 'Why Choose Saeed Taxi?'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <Card key={feature.title.en} className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-500 bg-white">
                <div className="text-black mb-4">{feature.icon}</div>
                <h4 className="text-xl font-semibold mb-2 text-gray-900">
                  {isArabic ? feature.title.ar : feature.title.en}
                </h4>
                <p className="text-gray-600">
                  {isArabic ? feature.description.ar : feature.description.en}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-black to-black">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-white mb-6">
            {isArabic ? 'جاهز للبدء؟' : 'Ready to Get Started?'}
          </h3>
          <p className="text-xl text-black mb-8">
            {isArabic 
              ? 'انضم إلى آلاف المستخدمين الذين يثقون بسعيد تاكسي للتنقل اليومي'
              : 'Join thousands of users who trust Saeed Taxi for their daily commute'
            }
          </p>
          <Button 
            size="lg"
            className="bg-white text-black hover:bg-gray-50 px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => navigate('/login')}
          >
            {t('bookRide')}
          </Button>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Car className="h-6 w-6" />
            <span className="text-2xl font-bold">{t('appName')}</span>
          </div>
          <p className="text-gray-400">
            © 2025 Saeed Taxi. {isArabic ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;